import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../store';
import { Image } from 'react-bootstrap';
import { api_url } from '../api/config/config';
import { useApi } from '../api/contexts/ApiContext';
import { Company } from '../types/type';
import { getTimeLeft, formatNumber } from '../utils/utils';
import { INDUSTRY_ENUM, INDUSTRY_ENUM_CATEGORY, CATEGORY } from '../config/const';
import { Category } from '../types/type';

interface PropsStructure {
    company: Company,
}

interface contract_usage {
    kW?: number,
    kVA?: number
}

const CompanyBlock: React.FC<PropsStructure> = ({ company }) => {
    const backendURL = api_url;

    const [contractStartDate, setContractStartDate] = useState<string>('');
    const [option4, setOption4] = useState<boolean>(true);
    const [planType, setPlanType] = useState<boolean>(true);
    const [contractUsages, setContractUsages] = useState<contract_usage>();
    const [contractType, setContractType] = useState<string | boolean>(company.facilities && company.facilities.length > 0 ? company.facilities[0].contract_type : false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [bidsCount, setBidsCount] = useState<number>();
    const [isEnded, setIsEnded] = useState<boolean>(false);
    const [favorite, setFavorite] = useState(company.favorite);
    const { userInfo } = useSelector((state: RootState) => state.auth);
    const { getModule } = useApi();

    // new Date().getTime() はミリ秒
    const nowInSeconds = Math.floor(new Date().getTime() / 1000);
    const tenDaysBeforeDue = nowInSeconds + (24 * 60 * 60 * 10);

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        const week = ['日', '月', '火', '水', '木', '金', '土'];
        return `${date.getFullYear()}年${(date.getMonth() + 1).toString().padStart(2, '0')}月${date.getDate().toString().padStart(2, '0')}日(${week[date.getDay()]})`;
    };


    useEffect(() => {
        if (!company || !company.facilities || company.facilities.length == 0) return;

        // 案件終了の判定（「案件終了」バッジと同じ基準：締切日(due)と選定終了日(close_date)の実日付を使用）
        setIsEnded(nowInSeconds > company.due && nowInSeconds > company.close_date);

        // contract usage
        const contract_usage = {
            kw: 0,
            kVA: 0
        };

        company.facilities.forEach(f => {
            if (!f.category) return;

            const categoryKey = f.category.toString();
            const categoryInfo = CATEGORY[categoryKey];
            if (!categoryInfo) return;

            const usage = Number(f.contract_usage) || 0;
            if (usage === 0) return;

            if (["特別高圧", "高圧電力", "低圧電力"].includes(categoryInfo.name)) {
                contract_usage.kw = (contract_usage.kw || 0) + usage;
            }

            if (categoryInfo.name === "従量電灯") {
                contract_usage.kVA = (contract_usage.kVA || 0) + usage;
            }
        });

        setContractUsages(contract_usage);

        // contract start date
        if (company.min_contract_start_date < company.max_contract_start_date) {
            setContractStartDate(formatDate(company.min_contract_start_date) + ' ～');
        } else {
            setContractStartDate(formatDate(company.min_contract_start_date));
        }

        // setPlanType
        if (company.facilities && company.facilities.length > 0) {
            setPlanType(company.facilities[0].plan_type);
        }

        // option_4
        if (company.facilities && company.facilities.length > 0) {
            const option4Count = company.facilities.filter(f => f.option_4).length;
            setOption4(option4Count === company.facilities.length);
        }

        // contract type
        if (company.facilities && company.facilities.length > 0) {
            const contractTypesSet = new Set(company.facilities.map(f => f.contract_type));
            contractTypesSet.size === 1 ? setContractType(company.facilities[0].contract_type) : setContractType(false);
        }

        // category
        if (company.facilities && company.facilities.length > 0) {
            const categoriesSet = company.facilities
                .map(f => {
                    if (!f.category) return null;
                    const categoryKey = f.category.toString();
                    const categoryInfo = CATEGORY[categoryKey];
                    return categoryInfo ? { name: categoryInfo.name, color: categoryInfo.color } : null;
                })
                .filter((item): item is Category => item !== null)
                .filter((value, index, self) =>
                    index === self.findIndex((t) => (
                        t.name === value.name && t.color === value.color
                    ))
                );

            setCategories([...categoriesSet]);
        }

        if (company.bids_count != null) {
            setBidsCount(company.bids_count);
        }
    }, [company]);

    const handleFavorite = async () => {
        const generalApi = await getModule('GeneralApi');
        setFavorite(!favorite);

        if (userInfo) {
            await generalApi.toggleFavorite({ user_id: userInfo?.id, opportunity_id: company.id });
        }
    }

    return (
        <div className={`product-block ${!(nowInSeconds > company.due && nowInSeconds > company.close_date) ? "bg-white" : "bg-green-04"}`}>
            <div className="product-header">
                {nowInSeconds > company.due && nowInSeconds > company.close_date && (
                    <div className="text-center py-1 px-2 bg-green-02 text-xs text-white">案件終了</div>
                )}

                <div className="row mx-0">
                    {(company.due > tenDaysBeforeDue) && (
                        <Image src="/assets/images/mypage/new.svg" className="new-ribbon" />
                    )}

                    {getTimeLeft(company.due).timeLeft > 0 ? (
                        <div className="col-10 offset-1 text-center px-0">
                            <div className="fw-300">
                                <span className="text-red-theme text-md ml-lg-4 ml-3">
                                    {getTimeLeft(company.due).timeLeft_str}
                                </span>{getTimeLeft(company.due).unit}
                            </div>
                        </div>
                    ) : (
                        nowInSeconds > company.due && nowInSeconds < company.close_date && (
                            <div className="col-10 offset-1 text-center px-0">
                                <div className="fw-300">電力会社選定期間残り
                                    <span className="text-red-theme text-md ml-lg-4 ml-3">
                                        {getTimeLeft(company.close_date).timeLeft_str}
                                    </span>
                                    {getTimeLeft(company.close_date).unit}
                                </div>
                            </div>
                        )
                    )}

                    <div className={`col-1 px-0 text-right favorite star opportunity-id-${company.id}
                         ${favorite ? "active" : ""}
                         `}
                        data-opportunity-id={`${company.id}`} data-facility-id="0"
                        onClick={handleFavorite}></div>
                </div>
            </div>
            <Link to={isEnded ? "javascript:void(0);" : `/project_detail?opportunity_id=${company.id}`} className="product-block-link">
                <div className="product-body">
                    <div className="row mx-0 mb-3">
                        <div className="col-12 px-0">
                            <h2 className="product-title">
                                <div className="row mx-0 mb-3">
                                    <div className="col-3 m-0 p-0">
                                        <span className="text-xxs">NO. 1{company.id.toString().padStart(4, '0')}</span>
                                    </div>
                                    <div className="col-9 m-0 p-0">
                                        {company.company_name}
                                    </div>
                                </div>
                            </h2>
                            <div className="product-sub-title">
                                <span className="btn-secondary text-white px-2">{company.industry && INDUSTRY_ENUM_CATEGORY[company.industry] ? INDUSTRY_ENUM[INDUSTRY_ENUM_CATEGORY[company.industry]] : INDUSTRY_ENUM['UNCLASSIFIED']}</span>
                                {categories.map((category) => (
                                    <span key={category.name} className="ml-1 btn-secondary text-white px-2" style={{ backgroundColor: category.color }}>
                                        {category.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="row mx-0 mb-1 pt-2">
                        <div className="col-12 company-bar"> 企業情報 </div>
                    </div>
                    <div className="product-details">
                        <div className="row mx-0 product-detail-row">
                            <div className="product-detail-row-title">施設数</div>
                            <div className="">
                                <span className="product-detail-key">{company.facility_count}</span> 施設
                            </div>
                        </div>
                        <div className="row mx-0 product-detail-row">
                            <div className="product-detail-row-title">使用量電力</div>
                            <div className="">
                                <span className="product-detail-key">{formatNumber(company.usage_total)}</span> kWh
                            </div>
                        </div>
                        <div className="row mx-0 product-detail-row last">
                            <div className="product-detail-row-title">契約電力/受電容量</div>
                            <div className="">
                                {
                                    contractUsages && (() => {
                                        const entries = Object.entries(contractUsages).filter(([_, usage]) => usage && usage > 0);
                                        return entries.map(([unit, usage], idx) => (
                                            <React.Fragment key={unit}>
                                                <span className="product-detail-key">{usage}</span>
                                                <span className='mr-1'>{unit}</span>
                                                {entries.length > 1 && idx < entries.length - 1 && <span className="mx-1">/</span>}
                                            </React.Fragment>
                                        ));
                                    })()
                                }
                            </div>
                        </div>
                    </div>
                    <div className="row mx-0 mb-1 pt-2">
                        <div className="col-12 company-note-bar"> 企業要望事項 </div>
                    </div>
                    <div className="product-details">
                        <div className="row mx-0 product-detail-row">
                            <div className="product-detail-row-title">希望プラン</div>
                            <div className="ml-auto ">
                                {planType}
                            </div>

                        </div>
                        <div className="row mx-0 product-detail-row">
                            <div className="product-detail-row-title">再生可能エネルギー</div>
                            <span className="product-detail-key ml-auto mr-1">
                                <Image src={`/assets/images/other/icon-eco-${option4 ? "blue" : "ass"}.svg`} />
                            </span>
                            {option4 ? "要望する" : "要望しない"}
                        </div>
                        <div className="row mx-0 product-detail-row last">
                            <div className="product-detail-row-title">供給開始日</div>
                            <div className="">
                                <span className="product-detail-key date">{contractStartDate}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    )
}

export default CompanyBlock;